import { useState, useEffect } from 'react';
import { Search, Eye, Shield, Activity, Database, User, Download, AlertCircle, ExternalLink, AlertTriangle } from 'lucide-react';
import { useAuditLog, AuditLogEntry, ActionType, Category } from '../hooks/useAuditLog';
import { DeviceModal } from './DeviceModal';
import { useDevices } from '../hooks/useDevices';
import { Device } from '../lib/supabase';
import { format } from 'date-fns';

export function AuditLogs() {
  const { getAuditLogs } = useAuditLog();
  const { devices } = useDevices();
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Device modal state
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [isDeviceModalOpen, setIsDeviceModalOpen] = useState(false);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<Category | 'all'>('all');
  const [selectedActionType, setSelectedActionType] = useState<ActionType | 'all'>('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [exportScope, setExportScope] = useState<'current' | 'all'>('current');
  const [showExportWarning, setShowExportWarning] = useState(false);
  
  const ITEMS_PER_PAGE = 50;

  const categories: Category[] = ['authentication', 'device_management', 'admin_management', 'system_monitoring', 'security'];
  
  const actionTypes: ActionType[] = [
    'login', 'logout', 'view_unlock_code', 'copy_unlock_code', 'regenerate_unlock_code',
    'create_device', 'edit_device', 'delete_device', 'lock_device', 'unlock_device',
    'bulk_import_devices', 'bulk_lock_devices', 'bulk_unlock_devices', 'bulk_delete_devices',
    'admin_registration', 'admin_approval', 'admin_denial', 'view_admin_profile', 'edit_admin_profile',
    'view_audit_logs', 'system_status_check', 'run_db_backup', 'fetch_table_counts',
    'create_lock_message', 'update_lock_message', 'delete_lock_message', 'set_default_lock_message'
  ];

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const filters = {
        category: selectedCategory !== 'all' ? selectedCategory : undefined,
        actionType: selectedActionType !== 'all' ? selectedActionType : undefined,
        startDate: startDate ? new Date(startDate) : undefined,
        endDate: endDate ? new Date(endDate) : undefined,
        limit: ITEMS_PER_PAGE,
        offset: (currentPage - 1) * ITEMS_PER_PAGE
      };

      const { logs: fetchedLogs, error: fetchError } = await getAuditLogs(filters);
      
      if (fetchError) {
        setError('Failed to fetch audit logs');
        return;
      }

      setLogs(fetchedLogs);
      
      // Get total count for pagination (simplified for now)
      setTotalLogs(fetchedLogs.length >= ITEMS_PER_PAGE ? (currentPage * ITEMS_PER_PAGE) + 1 : (currentPage - 1) * ITEMS_PER_PAGE + fetchedLogs.length);
    } catch (err) {
      setError('An error occurred while fetching logs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [selectedCategory, selectedActionType, startDate, endDate, currentPage]);

  const filteredLogs = logs.filter(log => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      log.user_email?.toLowerCase().includes(searchLower) ||
      log.user_name?.toLowerCase().includes(searchLower) ||
      log.action_type.toLowerCase().includes(searchLower) ||
      log.resource_type?.toLowerCase().includes(searchLower) ||
      log.resource_id?.toLowerCase().includes(searchLower)
    );
  });

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'authentication': return <User className="w-4 h-4" />;
      case 'device_management': return <Database className="w-4 h-4" />;
      case 'admin_management': return <Shield className="w-4 h-4" />;
      case 'system_monitoring': return <Activity className="w-4 h-4" />;
      case 'security': return <AlertCircle className="w-4 h-4" />;
      default: return <Eye className="w-4 h-4" />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'authentication': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      case 'device_management': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'admin_management': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
      case 'system_monitoring': return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300';
      case 'security': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
    }
  };

  const getActionColor = (success: boolean) => {
    return success 
      ? 'text-green-600 dark:text-green-400' 
      : 'text-red-600 dark:text-red-400';
  };

  // Check if a log entry is device-related and clickable
  const isDeviceRelated = (log: AuditLogEntry) => {
    return log.resource_type === 'device' && log.resource_id;
  };

  // Handle clicking on a device-related log entry
  const handleDeviceClick = (log: AuditLogEntry) => {
    if (!isDeviceRelated(log) || !log.resource_id) return;
    
    const device = devices.find(d => d.id === log.resource_id);
    if (device) {
      setSelectedDevice(device);
      setIsDeviceModalOpen(true);
    }
  };

  // Handle saving device (for modal compatibility)
  const handleSaveDevice = async () => {
    // This is a view-only modal, so we don't need to save
    return { success: true };
  };

  // Handle export button click - show warning first
  const handleExportClick = () => {
    setShowExportWarning(true);
  };

  // Proceed with actual export after confirmation
  const proceedWithExport = async () => {
    setShowExportWarning(false);
    await exportToCsv();
  };

  const exportToCsv = async () => {
    let dataToExport = filteredLogs;
    
    // If exporting all pages, fetch all data
    if (exportScope === 'all') {
      try {
        const filters = {
          category: selectedCategory !== 'all' ? selectedCategory : undefined,
          actionType: selectedActionType !== 'all' ? selectedActionType : undefined,
          startDate: startDate ? new Date(startDate) : undefined,
          endDate: endDate ? new Date(endDate) : undefined,
          limit: 10000, // Large limit to get all data
          offset: 0
        };

        const { logs: allLogs, error: fetchError } = await getAuditLogs(filters);
        
        if (fetchError) {
          console.error('Failed to fetch all logs for export');
          dataToExport = filteredLogs; // Fallback to current page
        } else {
          // Apply client-side search filter
          dataToExport = allLogs.filter(log => {
            if (!searchTerm) return true;
            
            const searchLower = searchTerm.toLowerCase();
            return (
              log.user_email?.toLowerCase().includes(searchLower) ||
              log.user_name?.toLowerCase().includes(searchLower) ||
              log.action_type.toLowerCase().includes(searchLower) ||
              log.resource_type?.toLowerCase().includes(searchLower) ||
              log.resource_id?.toLowerCase().includes(searchLower)
            );
          });
        }
      } catch (err) {
        console.error('Error fetching all logs:', err);
        dataToExport = filteredLogs; // Fallback to current page
      }
    }

    const headers = ['Timestamp', 'User', 'Email', 'Action', 'Category', 'Resource', 'Success', 'IP Address'];
    const csvContent = [
      headers.join(','),
      ...dataToExport.map(log => [
        format(new Date(log.created_at), 'yyyy-MM-dd HH:mm:ss'),
        `"${log.user_name || ''}"`,
        `"${log.user_email || ''}"`,
        `"${log.action_type}"`,
        `"${log.category}"`,
        `"${log.resource_type || ''} ${log.resource_id || ''}".trim()`,
        log.success ? 'Success' : 'Failed',
        `"${log.ip_address || ''}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    const scopeText = exportScope === 'all' ? 'all_pages' : 'current_page';
    link.download = `audit_logs_${scopeText}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Audit Logs</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Comprehensive activity tracking and monitoring
          </p>
        </div>
        <div className="flex items-center space-x-3">
          {/* Export Scope Selector */}
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Export:
            </label>
            <select
              value={exportScope}
              onChange={(e) => setExportScope(e.target.value as 'current' | 'all')}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
            >
              <option value="current">Current Page</option>
              <option value="all">All Pages</option>
            </select>
          </div>
          
          {/* Export Button */}
          <button
            onClick={handleExportClick}
            className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Search
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search users, actions, resources..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>

          {/* Category Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Category
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value as Category | 'all')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>
                  {category.replace('_', ' ').toUpperCase()}
                </option>
              ))}
            </select>
          </div>

          {/* Action Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Action Type
            </label>
            <select
              value={selectedActionType}
              onChange={(e) => setSelectedActionType(e.target.value as ActionType | 'all')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="all">All Actions</option>
              {actionTypes.map(action => (
                <option key={action} value={action}>
                  {action.replace(/_/g, ' ').toUpperCase()}
                </option>
              ))}
            </select>
          </div>

          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Date Range
            </label>
            <div className="flex space-x-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
              />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="text-gray-600 dark:text-gray-400 mt-4">Loading audit logs...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="p-8 text-center">
            <Eye className="w-8 h-8 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">No audit logs found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Action
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Resource
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    IP Address
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredLogs.map((log) => (
                  <tr 
                    key={log.id} 
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                      isDeviceRelated(log) 
                        ? 'cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20' 
                        : ''
                    }`}
                    onClick={() => isDeviceRelated(log) && handleDeviceClick(log)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {format(new Date(log.created_at), 'MMM dd, yyyy HH:mm:ss')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {log.user_name || 'Unknown User'}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {log.user_email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {log.action_type.replace(/_/g, ' ').toUpperCase()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(log.category)}`}>
                        {getCategoryIcon(log.category)}
                        <span className="ml-1">{log.category.replace('_', ' ').toUpperCase()}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {log.resource_type && (
                        <div>
                          <div className="font-medium">{log.resource_type}</div>
                          {log.resource_id && (
                            <div className={`text-gray-500 dark:text-gray-400 ${isDeviceRelated(log) ? 'flex items-center space-x-2' : ''}`}>
                              <span>{log.resource_id}</span>
                              {isDeviceRelated(log) && (
                                <div title="Click to view device">
                                  <ExternalLink className="w-3 h-3 text-blue-500 dark:text-blue-400" />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-medium ${getActionColor(log.success)}`}>
                        {log.success ? 'Success' : 'Failed'}
                      </span>
                      {log.error_message && (
                        <div className="text-xs text-red-500 dark:text-red-400 mt-1">
                          {log.error_message}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {log.ip_address || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && !error && filteredLogs.length > 0 && (
          <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              <div>Showing {Math.min(filteredLogs.length, ITEMS_PER_PAGE)} of {totalLogs} results</div>
              {exportScope === 'all' && (
                <div className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
                  CSV export will include all matching records across all pages
                </div>
              )}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-sm text-gray-700 dark:text-gray-300">
                Page {currentPage}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={filteredLogs.length < ITEMS_PER_PAGE}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Device Modal */}
      <DeviceModal
        isOpen={isDeviceModalOpen}
        onClose={() => {
          setIsDeviceModalOpen(false);
          setSelectedDevice(null);
        }}
        device={selectedDevice}
        mode="view"
        onSave={handleSaveDevice}
      />

      {/* Export Warning Modal */}
      {showExportWarning && (
        <div className="modal-overlay">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-8 h-8 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Export Database Usage Warning
              </h3>
            </div>
            
            <div className="space-y-3 mb-6">
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  <strong>Scope:</strong> {exportScope === 'all' ? 'All Pages' : 'Current Page Only'}
                </p>
                <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">
                  <strong>Estimated Records:</strong> {exportScope === 'all' ? `Up to 10,000 records (~${(10000 * 0.5 / 1024).toFixed(1)} MB)` : `${filteredLogs.length} records (~${(filteredLogs.length * 0.5 / 1024).toFixed(1)} MB)`}
                </p>
              </div>
              
              <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                <p>
                  <strong>Database Impact:</strong> This export will consume database egress resources, and count towards your egress limit for this quarter.
                </p>
                {exportScope === 'all' && (
                  <p className="text-amber-700 dark:text-amber-300">
                    <strong>⚠️ All Pages:</strong> This will fetch all matching audit logs across the entire database, which may consume significant resources.
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => setShowExportWarning(false)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={proceedWithExport}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Proceed with Export
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
